import React, { useContext, useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, CardHeader, Divider, FormControlLabel, Checkbox, TextField, MenuItem, Typography, Alert } from '@mui/material';
import { AuthContext } from './Auth';
import { collection, query, where, getDocs, addDoc, serverTimestamp, Timestamp } from 'firebase/firestore';

const CampaignComposer = ({ functionsInstance }) => {
  const { firestoreInstance, authUser } = useContext(AuthContext);
  const [funnels, setFunnels] = useState([]);
  const [selectedFunnel, setSelectedFunnel] = useState('');
  const [selectAll, setSelectAll] = useState(true);
  const [message, setMessage] = useState('');
  const [scheduleAt, setScheduleAt] = useState('');
  const [scheduling, setScheduling] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    const loadFunnels = async () => {
      if (!firestoreInstance || !authUser || !authUser.user) return;
      try {
        const q = query(collection(firestoreInstance, 'funnels'), where('owner', '==', authUser.user.uid));
        const snap = await getDocs(q);
        const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setFunnels(items);
        if (items.length && !selectedFunnel) setSelectedFunnel(items[0].id);
      } catch (e) {
        console.warn('Failed to load funnels', e);
      }
    };
    loadFunnels();
  }, [firestoreInstance, authUser]);

  const charCount = message.length;

  const handleSend = async (isSchedule) => {
    setError(null);
    setSuccess(null);
    if (!authUser || !authUser.user) return setError('Not authenticated');
    if (!selectedFunnel) return setError('Please select a funnel');
    if (!message || message.trim().length === 0) return setError('Message cannot be empty');

    setScheduling(true);
    try {
      const scheduled_at = isSchedule && scheduleAt ? Timestamp.fromDate(new Date(scheduleAt)) : (isSchedule ? null : serverTimestamp());
      const doc = {
        owner: authUser.user.uid,
        funnel_id: selectedFunnel,
        message: message.trim(),
        scheduled_at: scheduled_at,
        sent_at: null,
        status: isSchedule ? 'scheduled' : 'scheduled',
        created_at: serverTimestamp()
      };

      // Persist campaign
      await addDoc(collection(firestoreInstance, 'campaigns'), doc);
      setSuccess(isSchedule ? 'Campaign scheduled' : 'Campaign queued to send now');
      setMessage('');
      setScheduleAt('');
    } catch (e) {
      console.error('Failed to create campaign', e);
      setError(e && e.message ? e.message : String(e));
    } finally {
      setScheduling(false);
    }
  };

  return (
    <Card>
      <CardHeader title="Campaign Composer" />
      <CardContent>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 1 }}>{success}</Alert>}

        <TextField select fullWidth label="Funnel" value={selectedFunnel} onChange={e => setSelectedFunnel(e.target.value)} sx={{ mb: 2 }}>
          {funnels.map(f => (
            <MenuItem key={f.id} value={f.id}>{f.tweet_content ? (f.tweet_content.slice(0,60) + (f.tweet_content.length>60? '...':'')) : f.id}</MenuItem>
          ))}
        </TextField>

        <FormControlLabel control={<Checkbox checked={selectAll} onChange={e => setSelectAll(e.target.checked)} />} label="Select all subscribers" />

        <TextField
          multiline
          minRows={4}
          fullWidth
          label="Direct Message"
          value={message}
          onChange={e => setMessage(e.target.value)}
          helperText={`${charCount} characters`}
          sx={{ my: 2 }}
        />

        <Divider sx={{ my: 1 }} />

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', my: 1 }}>
          <TextField
            label="Schedule (optional)"
            type="datetime-local"
            InputLabelProps={{ shrink: true }}
            value={scheduleAt}
            onChange={e => setScheduleAt(e.target.value)}
            sx={{ width: 260 }}
          />
          <Button variant="outlined" onClick={() => { /* preview simply shows below */ }} >Preview</Button>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2">Preview</Typography>
          <Box sx={{ p: 1, bgcolor: '#f6f8fa', borderRadius: 1, minHeight: 56 }}>{message || <Typography color="text.secondary">Message preview will appear here</Typography>}</Box>
        </Box>

        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button variant="contained" onClick={() => handleSend(false)} disabled={scheduling}>Send Now</Button>
          <Button variant="outlined" onClick={() => handleSend(true)} disabled={scheduling || !scheduleAt}>Schedule</Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CampaignComposer;
